import {
  AudienceTagModeEnum,
  EnumOrganizationAppSettingsWidgetsEventOrganizerAudienceTagFiltersMode,
  EnumOrganizationAppSettingsWidgetsEventVenueAudienceTagFiltersMode,
  EnumOrganizationAppSettingsWidgetsOperatorAudienceTagFiltersMode,
  EnumOrganizationAppSettingsWidgetsWhatsNearbyAudienceTagFiltersMode,
} from "gql/generated";

export type AudienceTagMode = "include" | "exclude";

export type AudienceTagFilter = {
  tagId: string;
  mode: AudienceTagMode;
};

export type AudienceTagFilterInput = {
  tagId?: string | null;
  mode?: string | null;
};

function parseAudienceTagMode(
  mode: string | null | undefined,
): AudienceTagMode | null {
  if (mode === "include" || mode === "INCLUDE") {
    return "include";
  }
  if (mode === "exclude" || mode === "EXCLUDE") {
    return "exclude";
  }
  return null;
}

export function normalizeAudienceTagFilters(
  filters?: ReadonlyArray<AudienceTagFilterInput | null> | null,
  legacyIncludeIds?: ReadonlyArray<string | null | undefined> | null,
): AudienceTagFilter[] {
  if (filters && filters.length > 0) {
    return filters.flatMap(filter => {
      if (!filter?.tagId) return [];
      const mode = parseAudienceTagMode(filter.mode);
      if (!mode) return [];
      return [{ tagId: String(filter.tagId), mode }];
    });
  }

  if (legacyIncludeIds && legacyIncludeIds.length > 0) {
    return legacyIncludeIds
      .filter((id): id is string => !!id)
      .map(tagId => ({ tagId, mode: "include" as const }));
  }

  return [];
}

export function operatorMatchesAudienceTagFilters(
  operatorTagIds: string[],
  filters: AudienceTagFilter[],
): boolean {
  if (filters.length === 0) return true;

  const includeIds = filters
    .filter(f => f.mode === "include")
    .map(f => f.tagId);
  const excludeIds = filters
    .filter(f => f.mode === "exclude")
    .map(f => f.tagId);

  if (excludeIds.some(id => operatorTagIds.includes(id))) {
    return false;
  }

  if (includeIds.length === 0) {
    return true;
  }

  return operatorTagIds.some(id => includeIds.includes(id));
}

export function audienceTagFiltersToLegacyIncludeIds(
  filters: AudienceTagFilter[],
): string[] {
  return filters.filter(f => f.mode === "include").map(f => f.tagId);
}

export function audienceTagFiltersEqual(
  a: AudienceTagFilter[],
  b: AudienceTagFilter[],
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function audienceTagFiltersToOperatorInput(
  filters: AudienceTagFilter[],
) {
  return filters.map(filter => ({
    tagId: filter.tagId,
    mode:
      filter.mode === "include"
        ? EnumOrganizationAppSettingsWidgetsOperatorAudienceTagFiltersMode.Include
        : EnumOrganizationAppSettingsWidgetsOperatorAudienceTagFiltersMode.Exclude,
  }));
}

/** Maps app-level include/exclude filters to operatorMany GraphQL input. */
export function audienceTagFiltersToOperatorManyInput(
  filters: AudienceTagFilter[],
) {
  return filters.map(filter => ({
    tagId: filter.tagId,
    mode:
      filter.mode === "include"
        ? AudienceTagModeEnum.Include
        : AudienceTagModeEnum.Exclude,
  }));
}

export function audienceTagFiltersToWhatsNearbyInput(
  filters: AudienceTagFilter[],
) {
  return filters.map(filter => ({
    tagId: filter.tagId,
    mode:
      filter.mode === "include"
        ? EnumOrganizationAppSettingsWidgetsWhatsNearbyAudienceTagFiltersMode.Include
        : EnumOrganizationAppSettingsWidgetsWhatsNearbyAudienceTagFiltersMode.Exclude,
  }));
}

export function audienceTagFiltersToEventOrganizerInput(
  filters: AudienceTagFilter[],
) {
  return filters.map(filter => ({
    tagId: filter.tagId,
    mode:
      filter.mode === "include"
        ? EnumOrganizationAppSettingsWidgetsEventOrganizerAudienceTagFiltersMode.Include
        : EnumOrganizationAppSettingsWidgetsEventOrganizerAudienceTagFiltersMode.Exclude,
  }));
}

export function audienceTagFiltersToEventVenueInput(
  filters: AudienceTagFilter[],
) {
  return filters.map(filter => ({
    tagId: filter.tagId,
    mode:
      filter.mode === "include"
        ? EnumOrganizationAppSettingsWidgetsEventVenueAudienceTagFiltersMode.Include
        : EnumOrganizationAppSettingsWidgetsEventVenueAudienceTagFiltersMode.Exclude,
  }));
}
